import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
	getAdminBrand,
	listAdminBrandRows,
} from "@/features/catalog/services/catalog-api.service";
import { useDebounce } from "@/lib/use-debounce";

/**
 * The company that owns this brand, as another brand row.
 *
 * Searched rather than listed, like the merge dialog's target: the brand
 * master is 861 rows. The brand itself and merged-away brands are not offered
 * -- the server refuses the first, and the second is a record rather than an
 * owner.
 *
 * The list behaves like `CategoryPicker`'s: arrow keys and Enter choose,
 * Escape or a click or tab elsewhere closes it, and a failed search says so
 * rather than reading as "no brands match".
 */
export function ParentBrandField({
	brandId,
	value,
	onChange,
	disabled,
}: {
	/** Undefined on the create screen, where there is no row to exclude yet. */
	brandId?: string;
	value: string | null;
	onChange: (parentBrandId: string | null) => void;
	disabled?: boolean;
}) {
	const [search, setSearch] = useState("");
	/** The last choice, so its name shows before a save round-trips it. */
	const [picked, setPicked] = useState<{ id: string; name: string } | null>(
		null,
	);
	const [open, setOpen] = useState(false);
	/** -1 is "none", so Enter never commits a brand the operator has not seen. */
	const [activeIndex, setActiveIndex] = useState(-1);
	const containerRef = useRef<HTMLDivElement>(null);
	const term = useDebounce(search, 300).trim();

	const knownName = picked && picked.id === value ? picked.name : null;
	const parent = useQuery({
		queryKey: ["admin-brand", value],
		queryFn: () => getAdminBrand(value as string),
		enabled: Boolean(value) && !knownName,
	});
	const parentName = knownName ?? parent.data?.name ?? null;

	const candidates = useQuery({
		queryKey: ["admin-brands", "parent-candidates", term],
		queryFn: () => listAdminBrandRows({ q: term }),
		enabled: term.length > 0,
	});
	const options = (candidates.data?.items ?? []).filter(
		(candidate) => candidate.id !== brandId && !candidate.mergedIntoId,
	);
	// Matched but not on this page. Subtracted from the page, not from
	// `options`, so rows this component filtered out are not counted as hidden.
	const hiddenMatches = Math.max(
		0,
		(candidates.data?.total ?? 0) - (candidates.data?.items.length ?? 0),
	);

	useEffect(() => {
		if (!open) return;
		const closeIfOutside = (event: Event) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", closeIfOutside);
		document.addEventListener("focusin", closeIfOutside);
		return () => {
			document.removeEventListener("mousedown", closeIfOutside);
			document.removeEventListener("focusin", closeIfOutside);
		};
	}, [open]);

	function choose(option: { id: string; name: string }): void {
		setPicked({ id: option.id, name: option.name });
		onChange(option.id);
		setSearch("");
		setOpen(false);
		setActiveIndex(-1);
	}

	const listId = "parent-brand-results";

	return (
		<div className="grid gap-1.5 md:col-span-2">
			<Label htmlFor="parent-brand-search">Parent brand</Label>
			<div className="flex items-center gap-2 text-sm">
				{value ? (
					<>
						<span className="font-medium">{parentName ?? "Loading…"}</span>
						{disabled ? null : (
							<Button
								variant="outline"
								size="sm"
								onClick={() => {
									setPicked(null);
									onChange(null);
								}}
							>
								Clear parent
							</Button>
						)}
					</>
				) : (
					<span className="text-muted-foreground">None</span>
				)}
			</div>
			{disabled ? null : (
				<div ref={containerRef} className="relative">
					<Input
						id="parent-brand-search"
						placeholder={
							value ? "Search to change it" : "Search brands by name"
						}
						value={search}
						role="combobox"
						aria-expanded={open && term.length > 0}
						aria-controls={listId}
						aria-autocomplete="list"
						aria-activedescendant={
							activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined
						}
						onChange={(event) => {
							setSearch(event.target.value);
							setOpen(true);
							setActiveIndex(-1);
						}}
						onKeyDown={(event) => {
							if (event.key === "Escape" && open) {
								event.stopPropagation();
								setOpen(false);
								setActiveIndex(-1);
								return;
							}
							if (!open || options.length === 0) return;
							if (event.key === "ArrowDown") {
								event.preventDefault();
								setActiveIndex((index) => (index + 1) % options.length);
							} else if (event.key === "ArrowUp") {
								event.preventDefault();
								setActiveIndex(
									(index) => (index <= 0 ? options.length : index) - 1,
								);
							} else if (event.key === "Enter" && activeIndex >= 0) {
								event.preventDefault();
								choose(options[activeIndex]);
							}
						}}
					/>
					{open && term ? (
						<div
							id={listId}
							role="listbox"
							aria-label="Parent brand results"
							className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-white shadow-md"
						>
							{candidates.isError ? (
								<p className="p-3 text-sm text-red-700">
									Could not search brands.{" "}
									<button
										type="button"
										className="underline"
										onClick={() => candidates.refetch()}
									>
										Try again
									</button>
								</p>
							) : candidates.isPending || candidates.isFetching ? (
								<p className="p-3 text-sm text-muted-foreground">Searching…</p>
							) : options.length === 0 ? (
								<p className="p-3 text-sm text-muted-foreground">
									{hiddenMatches > 0
										? `None of the first matches can be a parent, and ${hiddenMatches} more match. Type more of the name.`
										: "No brands match that search."}
								</p>
							) : (
								<>
									{options.map((option, index) => (
										<button
											key={option.id}
											id={`${listId}-option-${index}`}
											type="button"
											role="option"
											aria-selected={index === activeIndex}
											className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-100 focus:bg-slate-100 focus:outline-none${
												index === activeIndex ? " bg-slate-100" : ""
											}`}
											onMouseEnter={() => setActiveIndex(index)}
											onClick={() => choose(option)}
										>
											{option.name}
										</button>
									))}
									{hiddenMatches > 0 ? (
										<p className="p-3 text-xs text-muted-foreground">
											{hiddenMatches} more match. Type more of the name.
										</p>
									) : null}
								</>
							)}
						</div>
					) : null}
				</div>
			)}
			<p className="text-xs text-muted-foreground">
				For a brand the brand master lists, the next seed run replaces this with
				the parent the file names, or with none if it names no brand.
			</p>
		</div>
	);
}
